import { SpaceType } from "@components/atoms/Spacer/types";
import { VDBText } from "@components/atoms";
import React from "react";
import styled, { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import * as Assets from "./utils/assets";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import { Platform, Image as RNImage } from "react-native";
import { Image as ExpoImage } from "expo-image";
import { DefaultTheme } from "styled-components/native";
import { useEnableProductSpecificFilterButtonColor } from "@redux/slices/globalSlice";

type ImageTextFilterItemProps = {
  item: any;
  screenWidth: number;
  isSelected: boolean;
  selectedStyle: SelectedItemTheme;
  width: number;
  isHovered: boolean;
};

const ImageTextFilterItem = ({
  item,
  isSelected,
  selectedStyle,
  width,
  isHovered,
}: ImageTextFilterItemProps) => {
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useUserInfo();
  const ImageComponent = Platform.OS === "ios" ? RNImage : ExpoImage;
  const enableProductSpecificFilterButtonColor =
    useEnableProductSpecificFilterButtonColor();
  const renderIcon = () => {
    if (item?.icon?.type === "font") {
      return (
        <VDBIcon
          name={item?.icon?.data || "warning"}
          variant={IconsVariant.VDBCustom}
          size={48}
          color={
            isSelected
              ? user?.custom_theme && !enableProductSpecificFilterButtonColor
                ? theme["option-cont-active"]
                : theme["core-05"]
              : theme["option-cont"]
          }
        />
      );
    } else if (item?.icon?.type === "url") {
      return (
        <VDBIcon
          name={undefined}
          variant={IconsVariant.CustomUrl}
          uri={item?.icon?.data}
          size={47}
          color={
            isSelected
              ? user?.custom_theme && !enableProductSpecificFilterButtonColor
                ? theme["option-cont-active"]
                : theme["core-05"]
              : theme["option-cont"]
          }
        />
      );
    } else if (item?.icon?.data !== "") {
      return (
        // @ts-ignore
        <ImageComponent
          style={{ height: 48, width: 48 }}
          {...(Platform.OS === "ios"
            ? { resizeMode: "contain" }
            : { contentFit: "contain" })}
          source={Assets[item?.icon?.data as keyof typeof Assets]}
        />
      );
    }
    return undefined;
  };

  const renderText = () => (
    <VDBText
      numberOfLines={1}
      isBold={true}
      type={typography["Minor-M"]}
      color={
        isSelected
          ? user?.custom_theme && !enableProductSpecificFilterButtonColor
            ? theme["option-cont-active"]
            : theme["core-05"]
          : theme["option-cont"]
      }
    >
      {t(item.label)}
    </VDBText>
  );

  return (
    <SquareSelect
      isSelected={isSelected}
      selectedStyle={selectedStyle}
      customTheme={
        !!user?.custom_theme && !enableProductSpecificFilterButtonColor
      }
      isPlaceholder={!item.label}
      width={width}
      isHover={isHovered}
    >
      {item?.icon && renderIcon()}
      {item?.icon && item?.icon?.data !== "" ? (
        renderText()
      ) : (
        <TextContainer>{renderText()}</TextContainer>
      )}
    </SquareSelect>
  );
};

const SquareSelect = styled.View<{
  isSelected: boolean;
  selectedStyle: SelectedItemTheme;
  customTheme: boolean;
  isPlaceholder: boolean;
  width: number;
  isHover: boolean;
}>`
  align-items: center;
  justify-content: center;
  background: ${({ theme }: { theme: DefaultTheme }) =>
    theme["option-bg-static"]};
  border-radius: ${SpaceType.Basic}px;
  width: ${({ width }: { width: number }) => width}px;
  padding-top: ${SpaceType.Tiny}px;
  padding-left: ${SpaceType.Tiny}px;
  padding-right: ${SpaceType.Tiny}px;
  padding-bottom: ${SpaceType.ExtraSmall}px;
  background-color: ${({
    isSelected,
    theme,
    selectedStyle,
    isPlaceholder,
    isHover,
  }: {
    isSelected: boolean;
    theme: DefaultTheme;
    selectedStyle: SelectedItemTheme;
    isPlaceholder: boolean;
    isHover: boolean;
  }) =>
    isPlaceholder
      ? "transparent"
      : isSelected
      ? theme[selectedStyle.primary as keyof DefaultTheme]
      : isHover
      ? theme["option-bg-hover"]
      : theme["option-bg-static"]};
  border-width: ${({ isPlaceholder }: { isPlaceholder: boolean }) =>
    isPlaceholder ? 0 : 1}px;
  border-color: ${({
    isSelected,
    theme,
    selectedStyle,
    customTheme,
  }: {
    isSelected: boolean;
    theme: DefaultTheme;
    selectedStyle: SelectedItemTheme;
    customTheme: boolean;
  }) =>
    isSelected
      ? customTheme
        ? theme[selectedStyle.primary as keyof DefaultTheme]
        : theme[selectedStyle.secondary as keyof DefaultTheme]
      : theme["option-bg-static"]};
`;

const TextContainer = styled.View`
  align-items: center;
  height: 64px;
  justify-content: center;
`;

export default ImageTextFilterItem;
